import { useQuery } from '@tanstack/react-query';
import {
  getPadron,
  getResumenPadron,
  listAsociadosOfPadron,
  listLegajos,
} from '@lib/services/padrones.service';

/** Pestañas del detalle de padrón. Fuente única: la usa también PadronDetalle. */
export type Tab = 'errores' | 'advertencias' | 'todos' | 'legajos';

/**
 * Bundle de queries del detalle de padrón. Lo encapsulamos en un hook para
 * sacar todas las condiciones `enabled: a && b` del cuerpo de la página
 * (cada operador lógico sumaba +1 a la complejidad cognitiva del componente,
 * que excedía el umbral de Sonar S3776). Los queries son los mismos que antes,
 * solo cambia el lugar donde viven.
 */
export function usePadronData(args: { id: string; tab: Tab; search: string }) {
  const { id, tab, search } = args;

  const padron = useQuery({
    queryKey: ['padron', id],
    queryFn: () => getPadron(id),
    enabled: !!id,
  });

  const filtroEstado = tab === 'errores' ? 'error' : undefined;
  const soloAdvertencias = tab === 'advertencias';
  const mostrandoTabAsociados =
    tab === 'todos' || tab === 'errores' || tab === 'advertencias';
  const asociadosEnabled = Boolean(id) && mostrandoTabAsociados;
  const asociados = useQuery({
    queryKey: ['asociados', id, filtroEstado, soloAdvertencias, search],
    queryFn: () =>
      listAsociadosOfPadron(id, {
        estado: filtroEstado,
        conAdvertencia: soloAdvertencias || undefined,
        search: search || undefined,
        limit: 200,
      }),
    enabled: asociadosEnabled,
  });

  const resumenLegajos = useQuery({
    queryKey: ['resumen-padron', id],
    queryFn: () => getResumenPadron(id),
    enabled: !!id,
  });

  const padronCerrado = padron.data?.estado === 'cerrado';
  const padronEnValidacion = padron.data?.estado === 'pendiente_validacion';
  const padronYaCerrado = padronCerrado || padronEnValidacion;
  const legajosEnabled = Boolean(id) && padronYaCerrado;
  const legajos = useQuery({
    queryKey: ['legajos', id],
    queryFn: () => listLegajos(id),
    enabled: legajosEnabled,
  });

  return {
    padron,
    asociados,
    resumenLegajos,
    legajos,
    padronCerrado,
    padronEnValidacion,
    filtroEstado,
  };
}
